// import debounce from 'lodash.debounce';
import Button from '2-core-elements/Buttons/Button';
import { ListingLoader } from '3-components/ListingApp/ListingUI/ListingUI';
import GoogleMapReact, { type MapOptions } from 'google-map-react';
import { useEffect, useRef, useState } from 'react';
import useSupercluster from 'use-supercluster';
import { BREAKPOINT_MD } from 'utils/breakpoint';
import ClusterPin from './ClusterPin';
import ItemPin, { getSkinByIconSet, type ItemPinProps } from './ItemPin';
import type { Country, MapItem } from './MapTypes';

type FeatureStyle = {
    fillColor: string;
    strokeColor: string;
    strokeWeight: number;
    strokeOpacity: number;
    fillOpacity: number;
};

type FeatureStyles = {
    [key: number]: FeatureStyle;
};

export interface MapProps<T> {
    apiKey: string;
    mapId?: string;
    items: T[];
    settings: {
        defaultMapBounds: number[];
        minZoom?: number;
        desktopMinZoom?: number;
        maxZoom?: number;
        defaultMapCenter: {
            lat: number;
            lng: number;
        };
        restriction: {
            latLngBounds: {
                east: number;
                north: number;
                south: number;
                west: number;
            };
            strictBounds?: boolean;
        };
        clusterRadius?: number;
        featureStyles?: FeatureStyles;
        iconSet?: ItemPinProps['iconSet'];
    };
    activeItemId: string;
    setActiveItemId: React.Dispatch<React.SetStateAction<string>>;
    resetZoomOnItemChange?: boolean;
    showInactiveItems?: boolean;
    showLoader?: boolean;
    resetMapZoomRef?: React.MutableRefObject<(() => void) | null>;
}

const getDefaultMapZoom = (minZoom: number, desktopMinZoom: number): number => {
    return window.innerWidth < 480 ? minZoom : desktopMinZoom;
};

const ListingMap = ({
    apiKey,
    mapId,
    items,
    settings,
    activeItemId,
    setActiveItemId,
    resetZoomOnItemChange = true,
    showInactiveItems = false,
    showLoader= false,
    resetMapZoomRef,
}: MapProps<MapItem>): JSX.Element => {
    const { minZoom = 0.6, desktopMinZoom = 1.8, maxZoom = 22 } = settings;
    const [bounds, setBounds] = useState<number[] | null>(settings.defaultMapBounds);
    const [zoom, setZoom] = useState(getDefaultMapZoom(minZoom, desktopMinZoom));
    const [center, setCenter] = useState(settings.defaultMapCenter);
    const [animationClass, setAnimationClass] = useState('animation-on');

    const mapRef = useRef<google.maps.Map>();

    const resetZoom = (): void => {
        setZoom(getDefaultMapZoom(minZoom, desktopMinZoom));
        setCenter(settings.defaultMapCenter);
    };

    const mapOptions: google.maps.MapOptions = {
        mapId,
        minZoom,
        maxZoom,
        restriction: settings.restriction,
    };

    const points = items.map(item => {
        return {
            // type: 'Feature',
            properties: { cluster: false },
            geometry: {
                type: 'Point',
                coordinates: [
                    item.coordinates.lon,
                    item.coordinates.lat
                ],
            },
            data: item,
        };
    });

    const { clusters, supercluster } = useSupercluster({
        points,
        bounds,
        zoom,
        options: { radius: (settings.clusterRadius ?? 80), maxZoom },
    });

    // const prevWindowWidth = useRef(0);

    // const resizeMap = () => {
    //     if (window.innerWidth <= 768) {
    //         setZoom(MOBILE_MAP_MIN_ZOOM);
    //     }
    // };

    // const onWindowResize = debounce(() => {
    //     // a method to prevent iOS to trigger resize on scroll
    //     if (prevWindowWidth.current !== window.innerWidth) {
    //         prevWindowWidth.current = window.innerWidth;

    //         return setTimeout(resizeMap, 300);
    //     }

    //     return null;
    // }, DEBOUNCE_VALUE);

    // useEffect(() => {
    //     prevWindowWidth.current = window.innerWidth;

    //     window.addEventListener('resize', onWindowResize);
    //     return () => {
    //         window.removeEventListener('resize', onWindowResize);
    //     };
    // }, []);

    useEffect(() => {
        if (resetMapZoomRef) {
            resetMapZoomRef.current = resetZoom;
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!activeItemId) {
            // zoom out when no active pin only happen on desktop
            if (window.innerWidth >= BREAKPOINT_MD && resetZoomOnItemChange) {
                setZoom(getDefaultMapZoom(minZoom, desktopMinZoom));
                setCenter(settings.defaultMapCenter);
            }
            document.body.classList.remove('mobile-modal-opened');
        } else {
            document.body.classList.add('mobile-modal-opened');
        }

        if (!mapRef.current || activeItemId) {
            return;
        }

        // when active item id changes to '' clear all the feature layer
        if (settings.featureStyles) {
            // when active item id changes to '' clear all the feature layer
            const featureLayer = mapRef.current.getFeatureLayer('COUNTRY' as google.maps.FeatureType);
            featureLayer.style = (): google.maps.FeatureStyleOptions | null => {
                return null;
            };
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeItemId]);

    useEffect(() => {
        if (activeItemId) {
            setAnimationClass('animation-off');
        } else {
            setAnimationClass('animation-on');
        }
    }, [items, activeItemId]);


    return (
        <div className={`listing-map ${animationClass}`}>
            {showLoader && <ListingLoader className="listing-map-loader" />}
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            <GoogleMapReact
                bootstrapURLKeys={{ key: apiKey }}
                defaultCenter={settings.defaultMapCenter}
                defaultZoom={getDefaultMapZoom(minZoom, desktopMinZoom)}
                yesIWantToUseGoogleMapApiInternals={true}
                onGoogleApiLoaded={({ map }): void => {
                    mapRef.current = map;
                    mapRef.current?.setClickableIcons(false);
                    setCenter(settings.defaultMapCenter);
                    // setZoom(getDefaultMapZoom(minZoom, desktopMinZoom));
                    setTimeout(() => {
                        setZoom(getDefaultMapZoom(minZoom, desktopMinZoom));
                    }, 300);
                }}
                zoom={zoom}
                center={center}
                debounced={false}
                // onTilesLoaded={onTilesLoaded}
                onChange={({ zoom: thisZoom, bounds: thisBounds, center: thisCenter }): void => {
                    setCenter(thisCenter);
                    setZoom(thisZoom);
                    setBounds([
                        thisBounds.nw.lng,
                        thisBounds.se.lat,
                        thisBounds.se.lng,
                        thisBounds.nw.lat
                    ]);
                }}
                // options={mapOptions}
                options={mapOptions as MapOptions}
            >
                {(!activeItemId || showInactiveItems) &&
                    clusters.map((cluster, index) => {
                        const [longitude, latitude] =
                            cluster.geometry.coordinates;
                        const { cluster: isCluster, point_count: pointCount } =
                            cluster.properties;

                        const animateStyle = { '--animation-order': index };

                        if (isCluster) {
                            return (
                                <ClusterPin
                                    key={`cluster-${cluster.id}`}
                                    lat={latitude}
                                    lng={longitude}
                                    onClick={(): void => {
                                        if (showLoader) {
                                            return;
                                        }

                                        const expansionZoom = Math.min(
                                            supercluster.getClusterExpansionZoom(
                                                cluster.id
                                            ),
                                            20
                                        );
                                        if (!mapRef?.current) {
                                            return;
                                        }
                                        // the order need to be zoom then pan, if it's inverted, it has bug zooming at the wrong place
                                        mapRef.current.setZoom(expansionZoom);
                                        mapRef.current.panTo({
                                            lat: latitude,
                                            lng: longitude,
                                        });
                                    }}
                                    style={animateStyle as React.CSSProperties}
                                >
                                    {pointCount}
                                </ClusterPin>
                            );
                        }

                        return (
                            <ItemPin
                                key={cluster.data.id}
                                lat={latitude}
                                lng={longitude}
                                icon={cluster.data.icon}
                                iconSet={settings.iconSet}
                                ariaLabel={cluster.data.name}
                                onClick={(): void => {
                                    if (!mapRef.current || showLoader) {
                                        return;
                                    }

                                    if (activeItemId === cluster.data.id) {
                                        setActiveItemId('');
                                    } else {
                                        if (settings.featureStyles) {
                                            const { featureStyles } = settings;
                                            const featureLayer = mapRef.current.getFeatureLayer('COUNTRY' as google.maps.FeatureType);
                                            featureLayer.style = (options): google.maps.FeatureStyleOptions | null => {
                                                // convert to array of placeIds for checking lateer
                                                const countryPlaceIds = (cluster.data.countries as Country[]).map(({ placeId }) => {
                                                    return placeId;
                                                });

                                                const currentFeatureStyles = featureStyles[getSkinByIconSet(cluster.data.icon, settings.iconSet)];

                                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                                if (countryPlaceIds.includes((options.feature as any).placeId) && currentFeatureStyles) {
                                                    return currentFeatureStyles;
                                                }

                                                return null;
                                            };
                                        }

                                        setActiveItemId(cluster.data.id);

                                        // zoom out when click on icon pin only happen on desktop
                                        if (window.innerWidth >= BREAKPOINT_MD && resetZoomOnItemChange) {
                                            setZoom(getDefaultMapZoom(minZoom, desktopMinZoom));
                                            // wait 300ms animation to finish resize map, then set center of it
                                            setTimeout(() => {
                                                setCenter({ lat: latitude, lng: longitude });
                                            }, 300);
                                        }
                                    }
                                }}
                                isActive={activeItemId === cluster.data.id}
                                style={animateStyle as React.CSSProperties}
                            />
                        );
                    })}
                {!!activeItemId && !showInactiveItems &&
                    items.map(({ id, name, coordinates: { lat, lon }, icon }) => {
                        return (
                            <ItemPin
                                key={id}
                                lat={lat}
                                lng={lon}
                                icon={icon}
                                iconSet={settings.iconSet}
                                ariaLabel={name}
                                isActive={true}
                                onClick={(): void => {
                                    if (showLoader) {
                                        return;
                                    }

                                    setActiveItemId('');
                                }}
                            />
                        );
                    })}
            </GoogleMapReact>
            <div className="listing-map-controls">
                <Button
                    skin="2" onlyIcon={true} prefixIcon="plus" ariaLabel="Zoom in" onClick={(): void => {
                        if (zoom < maxZoom) {
                            setZoom(currentZoom => {
                                return currentZoom + 0.6;
                            });
                        }
                    }}
                />
                <Button
                    skin="2" onlyIcon={true} prefixIcon="minus" ariaLabel="Zoom out" onClick={(): void => {
                        if (zoom > getDefaultMapZoom(minZoom, desktopMinZoom)) {
                            setZoom(currentZoom => {
                                return currentZoom - 0.6;
                            });
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default ListingMap;
